/* eslint-disable unicorn/numeric-separators-style */
import { POST_PREVIEW_SUPPORTED_TYPES_BY_PLATFORM } from "@dialogueconsulting/sked-preview";

import { FIELDS } from "../../../constants/PostFormFields";
import { PlatformSpec } from "../../../constants/Platforms";
import { AccountPlatformType } from "../../../shared/types/accounts";

export const RULES = {
  LENGTH: "CHARACTER_LENGTH",
  HASHTAG_COUNT: "HASHTAG_COUNT",
  HASHTAG_HARD_LIMIT: "HASHTAG_HARD_LIMIT",
  MENTION_COUNT: "MENTION_COUNT",
  SUPPORTED_POST_TYPE: "SUPPORTED_POST_TYPE",
  REQUIRED: "REQUIRED",
} as const;
export type RulesType = (typeof RULES)[keyof typeof RULES];

export const DEFAULT_HASHTAG_LIMIT = 30;
export const INSTAGRAM_HASHTAG_LIMIT = 5;
export const FACEBOOK_MENTION_LIMIT = 15;

function getRuleMaxForField(
  platformType: AccountPlatformType,
  field: string,
  rule: RulesType
) {
  const platformConfig =
    postPlatformsConfig[platformType as keyof typeof postPlatformsConfig];
  const validation = (platformConfig as any)?.validation;
  return (
    validation?.error?.[field]?.[rule]?.[1] ??
    validation?.warn?.[field]?.[rule]?.[1]
  );
}

export function getStrictestRuleMax({
  platforms,
  selectedPlatform,
  field,
  rule,
}: {
  platforms: PlatformSpec[];
  selectedPlatform?: AccountPlatformType | "LINKED";
  field: string;
  rule: RulesType;
}) {
  if (platforms.length > 0) {
    return platforms.reduce<number | undefined>((minValue, { TYPE }) => {
      const max = getRuleMaxForField(TYPE, field, rule);

      if (max === undefined) {
        return minValue;
      }

      if (minValue === undefined || minValue > max) {
        return max;
      }

      return minValue;
    }, undefined);
  }

  if (!selectedPlatform || selectedPlatform === "LINKED") {
    return undefined;
  }

  return getRuleMaxForField(selectedPlatform, field, rule);
}

export const postPlatformsConfig = {
  IG: {
    fields: [
      FIELDS.CAPTION,
      FIELDS.FIRST_COMMENT,
      FIELDS.URL,
      FIELDS.LOCATION_TAG,
      FIELDS.USER_TAGS,
      FIELDS.PRODUCT_TAGS,
      FIELDS.STORY_TYPE,
      FIELDS.STORY_URL,
      FIELDS.STORY_LABEL,
      FIELDS.STORY_STICKER_HEIGHT,
      FIELDS.ALT_TEXT,
      FIELDS.COLLABORATOR_TAG,
    ],
    validation: {
      error: {
        [FIELDS.TYPE]: {
          [RULES.SUPPORTED_POST_TYPE]:
            POST_PREVIEW_SUPPORTED_TYPES_BY_PLATFORM.IG,
        },
        [FIELDS.CAPTION]: {
          [RULES.LENGTH]: [0, 2200],
        },
        [FIELDS.FIRST_COMMENT]: {
          [RULES.LENGTH]: [0, 2200],
        },
        [FIELDS.MEDIA]: {
          [RULES.LENGTH]: [0, 10],
        },
      },
      /*
       * SKE-4499. Two thresholds measuring two different things.
       *
       * Five is Sked's editorial advice about total hashtag load, so it counts
       * the caption and the first comment together. Thirty is Meta's ceiling,
       * enforced by `IG_CAPTION_MAX_HASHTAGS` in
       * `packages/ig-content-publishing/src/containers/build.ts`, and that
       * counts the CAPTION ALONE: the first comment is posted separately once
       * the media id exists and never reaches the container. Hence caption-only
       * here, and no entry under `FIRST_COMMENT`.
       */
      warn: {
        [FIELDS.CAPTION]: {
          [RULES.HASHTAG_COUNT]: [0, INSTAGRAM_HASHTAG_LIMIT],
          [RULES.HASHTAG_HARD_LIMIT]: [0, DEFAULT_HASHTAG_LIMIT],
        },
        [FIELDS.FIRST_COMMENT]: {
          [RULES.HASHTAG_COUNT]: [0, INSTAGRAM_HASHTAG_LIMIT],
        },
      },
      types: [],
    },
  },
  TT: {
    fields: [
      FIELDS.CAPTION,
      FIELDS.URL,
      FIELDS.POST_IS_NOTIFICATION_ONLY,
      FIELDS.TT_ALLOW_COMMENTS,
      FIELDS.TT_ALLOW_STITCH,
      FIELDS.TT_ALLOW_DUET,
      FIELDS.TT_TITLE,
      FIELDS.TT_PRIVACY_LEVEL,
      FIELDS.TT_AUTO_ADD_MUSIC,
      FIELDS.TT_PHOTO_COVER_INDEX,
    ],
    validation: {
      error: {
        [FIELDS.TYPE]: {
          [RULES.SUPPORTED_POST_TYPE]:
            POST_PREVIEW_SUPPORTED_TYPES_BY_PLATFORM.TT,
        },
        [FIELDS.MEDIA]: {
          [RULES.LENGTH]: [0, 35],
        },
        [FIELDS.CAPTION]: {
          [RULES.LENGTH]: [0, 2200],
        },
        [FIELDS.TT_TITLE]: {
          [RULES.LENGTH]: [
            0,
            90,
            "Tiktok: Title - 90 characters are supported.",
          ],
        },
      },
      warn: {},
      types: [],
    },
  },
  SC: {
    fields: [FIELDS.CAPTION, FIELDS.URL, FIELDS.ALT_TEXT],
    validation: {
      error: {
        [FIELDS.TYPE]: {
          [RULES.SUPPORTED_POST_TYPE]:
            POST_PREVIEW_SUPPORTED_TYPES_BY_PLATFORM.SC,
        },
        [FIELDS.CAPTION]: {
          [RULES.LENGTH]: [0, 10000],
        },
        [FIELDS.MEDIA]: {
          [RULES.LENGTH]: [0, 20],
        },
      },
      warn: {},
      types: [],
    },
  },
  TH: {
    fields: [
      FIELDS.CAPTION,
      FIELDS.URL,
      FIELDS.TH_REPLY_CONTROL,
      FIELDS.POST_IS_NOTIFICATION_ONLY,
    ],
    validation: {
      error: {
        [FIELDS.TYPE]: {
          [RULES.SUPPORTED_POST_TYPE]:
            POST_PREVIEW_SUPPORTED_TYPES_BY_PLATFORM.TH,
        },
        [FIELDS.CAPTION]: {
          [RULES.LENGTH]: [0, 500],
        },
        [FIELDS.MEDIA]: {
          [RULES.LENGTH]: [0, 10],
        },
      },
      warn: {},
      types: [],
    },
  },
  OT: {
    fields: [FIELDS.CAPTION, FIELDS.URL, FIELDS.ALT_TEXT],
    validation: {
      error: {
        [FIELDS.TYPE]: {
          [RULES.SUPPORTED_POST_TYPE]:
            POST_PREVIEW_SUPPORTED_TYPES_BY_PLATFORM.OT,
        },
        [FIELDS.CAPTION]: {
          [RULES.LENGTH]: [0, 10000],
        },
        [FIELDS.MEDIA]: {
          [RULES.LENGTH]: [0, 20],
        },
      },
      warn: {},
      types: [],
    },
  },
  FB: {
    fields: [
      FIELDS.CAPTION,
      FIELDS.FIRST_COMMENT,
      FIELDS.URL,
      FIELDS.LOCATION_TAG,
      FIELDS.ALT_TEXT,
      // Stories: FB follows IG's type and manual publishing choices.
      FIELDS.STORY_TYPE,
      FIELDS.PUBLISH_STORY_MANUALLY,
    ],
    validation: {
      error: {
        [FIELDS.TYPE]: {
          [RULES.SUPPORTED_POST_TYPE]:
            POST_PREVIEW_SUPPORTED_TYPES_BY_PLATFORM.FB,
        },
        [FIELDS.CAPTION]: {
          [RULES.LENGTH]: [0, 63206],
          [RULES.HASHTAG_COUNT]: [0, DEFAULT_HASHTAG_LIMIT],
          [RULES.MENTION_COUNT]: [0, FACEBOOK_MENTION_LIMIT],
        },
        [FIELDS.MEDIA]: {
          [RULES.LENGTH]: [0, 10],
        },
      },
    },
  },
  TW: {
    fields: [FIELDS.CAPTION, FIELDS.URL, FIELDS.ALT_TEXT],
    validation: {
      error: {
        [FIELDS.TYPE]: {
          [RULES.SUPPORTED_POST_TYPE]:
            POST_PREVIEW_SUPPORTED_TYPES_BY_PLATFORM.TW,
        },
        [FIELDS.CAPTION]: {
          // Absolute X ceiling. The 280 vs 25,000 nuance (per-account X
          // Premium capability, warn-not-block) lives in validateTw().
          [RULES.LENGTH]: [0, 25000],
          [RULES.HASHTAG_COUNT]: [0, DEFAULT_HASHTAG_LIMIT],
        },
        [FIELDS.MEDIA]: {
          [RULES.LENGTH]: [0, 10],
        },
      },
      warn: {},
    },
  },
  LI: {
    fields: [FIELDS.CAPTION, FIELDS.FIRST_COMMENT, FIELDS.URL, FIELDS.ALT_TEXT],
    validation: {
      error: {
        [FIELDS.TYPE]: {
          [RULES.SUPPORTED_POST_TYPE]:
            POST_PREVIEW_SUPPORTED_TYPES_BY_PLATFORM.LI,
        },
        [FIELDS.CAPTION]: {
          [RULES.LENGTH]: [0, 3000],
          [RULES.HASHTAG_COUNT]: [0, DEFAULT_HASHTAG_LIMIT],
        },
        [FIELDS.MEDIA]: {
          [RULES.LENGTH]: [0, 10],
        },
      },
    },
    warn: {},
  },
  GMB: {
    fields: [
      FIELDS.CAPTION,
      FIELDS.GMB_TOPIC_TYPE,
      FIELDS.GMB_CTA,
      FIELDS.GMB_TITLE,
      FIELDS.GMB_START_DATE,
      FIELDS.GMB_END_DATE,
      FIELDS.URL,
      FIELDS.GMB_COUPON_CODE,
      FIELDS.GMB_TERMS,
    ],
    validation: {
      error: {
        [FIELDS.MEDIA]: {
          [RULES.LENGTH]: [0, 10],
        },
        [FIELDS.CAPTION]: {
          [RULES.LENGTH]: [0, 1500],
        },
      },
      warn: {},
    },
  },
  PI: {
    fields: [
      FIELDS.CAPTION,
      FIELDS.URL,
      FIELDS.PI_TITLE,
      FIELDS.PI_BOARDS,
      FIELDS.PI_SECTION_ID,
      FIELDS.ALT_TEXT,
    ],
    validation: {
      error: {
        [FIELDS.TYPE]: {
          [RULES.SUPPORTED_POST_TYPE]:
            POST_PREVIEW_SUPPORTED_TYPES_BY_PLATFORM.PI,
        },
        [FIELDS.CAPTION]: {
          [RULES.LENGTH]: [0, 500],
        },
        [FIELDS.PI_TITLE]: {
          [RULES.LENGTH]: [0, 100],
        },
      },
      warn: {},
    },
  },
  YT: {
    fields: [
      FIELDS.CAPTION,
      FIELDS.YT_TITLE,
      FIELDS.YT_CATEGORY,
      FIELDS.YT_TAGS,
      FIELDS.YT_PRIVACY,
      FIELDS.URL,
    ],
    validation: {
      error: {
        [FIELDS.TYPE]: {
          [RULES.SUPPORTED_POST_TYPE]:
            POST_PREVIEW_SUPPORTED_TYPES_BY_PLATFORM.YT,
        },
        [FIELDS.CAPTION]: {
          [RULES.LENGTH]: [0, 5500],
        },
        [FIELDS.YT_TITLE]: {
          [RULES.LENGTH]: [0, 100],
          [RULES.REQUIRED]: {
            message: "Title is required.",
            onValidateFull: true,
          },
        },
      },
      warn: {},
    },
  },
} as const;

export type PostPlatformConfigType = typeof postPlatformsConfig;
